import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, PlusIcon, EditIcon, TrashIcon, ShieldIcon, UserIcon, RefreshCwIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/api';

interface Profession {
  id: string;
  name: string;
  color: string;
  icon?: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  phone?: string;
  role: 'admin' | 'practitioner';
  profession_id?: string;
  profession?: Profession;
  whatsapp_connected: boolean;
  created_at: string;
}

export function UserManagement() {
  const navigate = useNavigate();
  const { session, profile } = useAuth();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Fetch users with automatic refresh to keep WhatsApp connection status up-to-date
  const { data: usersData, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const response = await apiRequest('/api/admin/users', {}, session?.access_token);
      return response.users as User[];
    },
    enabled: !!session?.access_token && profile?.role === 'admin',
    refetchInterval: 30000, // Refresh every 30 seconds to sync with WhatsApp connection status changes
    staleTime: 15000, // Consider data stale after 15 seconds
  });

  // Fetch professions for the dropdown
  const { data: professionsData } = useQuery({
    queryKey: ['admin', 'professions'],
    queryFn: async () => {
      const response = await apiRequest('/api/admin/professions', {}, session?.access_token);
      return response.professions as Profession[];
    },
    enabled: !!session?.access_token && profile?.role === 'admin',
  });

  const users = usersData || [];
  const professions = professionsData || [];

  if (!profile || profile.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-md">
          <p className="text-lg font-semibold text-gray-800 mb-2">Access Denied</p>
          <p className="text-gray-600 mb-6">You need admin privileges to access this page.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-[#5B8E87] text-white px-6 py-3 rounded-xl font-medium"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-30">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/admin')} className="text-gray-600 hover:text-gray-800">
                <ArrowLeftIcon className="w-6 h-6" />
              </button>
              <h1 className="text-xl font-semibold text-[#212121]">User Management</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                title="Refresh users"
              >
                <RefreshCwIcon className={`w-5 h-5 ${isFetching ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-[#5B8E87] text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-[#4F7A74] transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Add User
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 pb-24 max-w-4xl mx-auto">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B8E87] mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <p className="text-gray-600 mb-4">No users found</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#5B8E87] text-white px-6 py-3 rounded-xl font-medium"
            >
              Create First User
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {users.map((user) => (
              <div
                key={user.id}
                className="bg-white rounded-2xl shadow-sm p-4 hover:shadow-md transition-shadow"
              >
                {/* User Info */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-semibold text-[#212121] truncate">
                        {user.name}
                      </h3>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {user.role === 'admin' ? (
                          <ShieldIcon className="w-3 h-3" />
                        ) : (
                          <UserIcon className="w-3 h-3" />
                        )}
                        {user.role === 'admin' ? 'Admin' : 'Practitioner'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{user.email}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-2">
                    <button
                      onClick={() => setEditingUser(user)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit user"
                    >
                      <EditIcon className="w-4 h-4" />
                    </button>
                    {user.id !== profile?.id && (
                      <button
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete user"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Profession & WhatsApp Status */}
                <div className="flex items-center gap-2 flex-wrap">
                  {user.profession ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full">
                      {user.profession.icon && <span>{user.profession.icon}</span>}
                      {user.profession.name}
                    </span>
                  ) : (
                    <span className="inline-flex px-3 py-1 bg-gray-100 text-gray-400 text-sm rounded-full">
                      No profession
                    </span>
                  )}

                  <span
                    className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                      user.whatsapp_connected
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {user.whatsapp_connected ? '✓ WhatsApp' : '✗ WhatsApp'}
                  </span>

                  {user.phone && (
                    <span className="text-sm text-gray-500">{user.phone}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          professions={professions}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
            setEditingUser(null);
          }}
        />
      )}

      {/* TODO: Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h2 className="text-xl font-semibold text-[#212121] mb-4">Add New User</h2>
            <p className="text-gray-600 mb-4">User creation form coming soon...</p>
            <button
              onClick={() => setShowCreateModal(false)}
              className="w-full bg-gray-200 text-gray-800 px-4 py-3 rounded-xl font-medium"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Edit User Modal Component
function EditUserModal({
  user,
  professions,
  onClose,
  onSuccess,
}: {
  user: User;
  professions: Profession[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { session } = useAuth();
  const [formData, setFormData] = useState({
    name: user.name,
    phone: user.phone || '',
    role: user.role,
    profession_id: user.profession_id || '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      const updates: any = {};
      if (formData.name !== user.name) updates.name = formData.name;
      if (formData.phone !== user.phone) updates.phone = formData.phone || null;
      if (formData.role !== user.role) updates.role = formData.role;
      if (formData.profession_id !== user.profession_id) {
        updates.profession_id = formData.profession_id || null;
      }

      if (Object.keys(updates).length === 0) {
        onClose();
        return;
      }

      await apiRequest(
        `/api/admin/users/${user.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify(updates),
        },
        session?.access_token
      );

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold text-[#212121] mb-4">Edit User</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              required
            />
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={user.email}
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              placeholder="Optional"
            />
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'practitioner' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              required
            >
              <option value="practitioner">Practitioner</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Profession */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Profession
            </label>
            <select
              value={formData.profession_id}
              onChange={(e) => setFormData({ ...formData, profession_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
            >
              <option value="">No profession</option>
              {professions.map((profession) => (
                <option key={profession.id} value={profession.id}>
                  {profession.icon} {profession.name}
                </option>
              ))}
            </select>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-3 rounded-xl font-medium hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 bg-[#5B8E87] text-white px-4 py-3 rounded-xl font-medium hover:bg-[#4F7A74] transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
